// 偵測網格 `kinio_motion.zone_grid`（SPEC #ls2505-334）：NVR / Kinio Station 通道用，18 列 × 22 格。
// wire：18 個 22 字元 '0' / '1' 字串，列 0 是畫面最上面、格 0 是最左邊；1 = 偵測，全 0 視同全畫面（nvrd motion.area 原樣）。
// 參考實作 component/protocol `shapes.go` Grid；golden 在 testdata/zone_grid/（照抄 protocol 030705c）。形狀不對整個不收、不硬修。
// 編輯：拖過的格子塗上或擦掉，起點那格的反相決定這一筆是塗或擦；另有「全選」「清除」。

export const ZONE_GRID_KEY = 'kinio_motion.zone_grid';
export const GRID_ROWS = 18;
export const GRID_COLS = 22;

/** [列][格]，true = 偵測。 */
export type ZoneGrid = boolean[][];

const ROW_RE = /^[01]{22}$/;

/** wire → 格子；不是 18 個 22 字元的 '0'/'1' 字串 → null。 */
export function parseZoneGrid(raw: unknown): ZoneGrid | null {
  if (!Array.isArray(raw) || raw.length !== GRID_ROWS) return null;
  const out: ZoneGrid = [];
  for (const r of raw) {
    if (typeof r !== 'string' || !ROW_RE.test(r)) return null;
    out.push([...r].map((c) => c === '1'));
  }
  return out;
}

export function encodeZoneGrid(grid: ZoneGrid): string[] {
  return grid.map((row) => row.map((on) => (on ? '1' : '0')).join(''));
}

/** 全部同一個值的網格（「全選」= true、「清除」= false）。 */
export function filledZoneGrid(value: boolean): ZoneGrid {
  return Array.from({ length: GRID_ROWS }, () => Array<boolean>(GRID_COLS).fill(value));
}

/** 一格都沒塗（= 全畫面偵測）。 */
export function zoneGridEmpty(grid: ZoneGrid): boolean {
  return !grid.some((row) => row.some(Boolean));
}

export function zoneGridEqual(a: ZoneGrid, b: ZoneGrid): boolean {
  return a.length === b.length && a.every((row, i) => row.length === b[i].length && row.every((v, j) => v === b[i][j]));
}

export function zoneGridConverged(reported: unknown, sent: unknown): boolean {
  const r = parseZoneGrid(reported);
  const s = parseZoneGrid(sent);
  return r !== null && s !== null && zoneGridEqual(r, s);
}

/** 摘要：全畫面（全 0 或全 1）/ 部分；解析不了 → null。 */
export function zoneGridSummary(raw: unknown): 'full_frame' | 'custom' | null {
  const g = parseZoneGrid(raw);
  if (!g) return null;
  const flat = g.flat();
  return flat.every(Boolean) || !flat.some(Boolean) ? 'full_frame' : 'custom';
}

/** 這一筆要塗（true）還是擦（false）：起點那格的反相。 */
export function strokeValue(base: ZoneGrid, row: number, col: number): boolean {
  return !base[row]?.[col];
}

/** 把拖過的格子設成 value（超出範圍的格子略過）；回傳新的網格，不改 base。 */
export function paintCells(base: ZoneGrid, cells: readonly { row: number; col: number }[], value: boolean): ZoneGrid {
  const out = base.map((row) => [...row]);
  for (const { row, col } of cells) {
    if (row >= 0 && row < GRID_ROWS && col >= 0 && col < GRID_COLS) out[row][col] = value;
  }
  return out;
}
